import { Typography, Button } from "antd";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { LoanSchemaDto } from "dtos/loan.dto";
import { Dispatch, SetStateAction } from "react";
import { formatMoney } from "utils/functions";

const { Text } = Typography;

export type MonthlyBreakdown = NonNullable<LoanSchemaDto["monthly_breakdown"]>[number];

interface Props {
  data: MonthlyBreakdown[];
  showExtrapolated: boolean;
  setShowExtrapolated: Dispatch<SetStateAction<boolean>>;
}

const ACTUAL_COLOR = "#6366f1";
const EXTRAPOLATED_COLOR = "#a5b4fc";

const pickTicks = (data: MonthlyBreakdown[], maxTicks = 5): string[] => {
  if (data.length <= maxTicks) return data.map((d) => d.month_label);
  const result: string[] = [data[0].month_label];
  const step = (data.length - 1) / (maxTicks - 1);
  for (let i = 1; i < maxTicks - 1; i++) {
    result.push(data[Math.round(i * step)].month_label);
  }
  result.push(data[data.length - 1].month_label);
  return result;
};

const formatTick = (value: string): string => {
  if (!value) return "";
  // month_label format is "June 2026" — split on space
  const parts = value.split(" ");
  if (parts.length === 2) {
    const mon = parts[0].slice(0, 3);
    const yr = parts[1].slice(2); // "2026" → "26"
    return `${mon} '${yr}`;
  }
  return value.slice(0, 6);
};

type ChartRow = MonthlyBreakdown & {
  mca_actual: number | null;
  mca_extrapolated: number | null;
};

const splitByExtrapolation = (data: MonthlyBreakdown[]): ChartRow[] => {
  const lastActualIndex = data.reduce((acc, d, i) => (!d.is_extrapolated ? i : acc), -1);

  return data.map((d, i) => ({
    ...d,
    mca_actual: i <= lastActualIndex ? d.mca_amount : null,
    mca_extrapolated: i >= lastActualIndex ? d.mca_amount : null,
  }));
};

const ActualDot = (props: { cx?: number; cy?: number; payload?: ChartRow }) => {
  const { cx, cy, payload } = props;
  if (cx === undefined || cy === undefined || !payload) return null;
  if (payload.is_extrapolated) return null;
  return <circle cx={cx} cy={cy} r={3} fill={ACTUAL_COLOR} />;
};

const ExtrapolatedDot = (props: { cx?: number; cy?: number; payload?: ChartRow }) => {
  const { cx, cy, payload } = props;
  if (cx === undefined || cy === undefined || !payload) return null;
  if (!payload.is_extrapolated) return null;
  return <circle cx={cx} cy={cy} r={3} fill={EXTRAPOLATED_COLOR} />;
};

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string; payload: ChartRow }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 6,
        padding: "8px 10px",
        fontSize: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        width: 160,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6, color: "#374151" }}>{label}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <span style={{ color: "#6b7280" }}>Payments</span>
          <span style={{ fontWeight: 500, color: ACTUAL_COLOR }}>
            {row.is_extrapolated ? "~" : ""}
            {formatMoney(row.mca_amount ?? 0)}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <span style={{ color: "#6b7280" }}>Revenue</span>
          <span style={{ fontWeight: 500, color: "#ec4899" }}>
            {row.is_extrapolated ? "~" : ""}
            {formatMoney(row.true_revenue ?? 0)}
          </span>
        </div>
      </div>
    </div>
  );
};

export const MonthlyBreakdownChart = ({ data, showExtrapolated, setShowExtrapolated }: Props) => {
  if (!data.length) {
    return (
      <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          No breakdown data
        </Text>
      </div>
    );
  }

  const ticks = pickTicks(data, 5);
  const chartData = splitByExtrapolation(data);
  const extrapolatedCount = data.filter((d) => d.is_extrapolated).length;
  const hasExtrapolated = extrapolatedCount > 0;

  return (
    <div style={{ width: 280, paddingTop: 4 }}>
      {hasExtrapolated && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 6,
          }}
        >
          <Text type="secondary" style={{ fontSize: 11 }}>
            {extrapolatedCount}/{data.length} months&apos; data not found
          </Text>
          <Button
            size="small"
            type={showExtrapolated ? "primary" : "default"}
            onClick={() => setShowExtrapolated((v) => !v)}
            style={{ fontSize: 11, height: 22, padding: "0 8px" }}
          >
            Extrapolate?
          </Button>
        </div>
      )}

      <ResponsiveContainer width="100%" height={170}>
        <LineChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 24 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="month_label"
            ticks={ticks}
            tickFormatter={formatTick}
            tick={{ fontSize: 10, fill: "#9ca3af" }}
            angle={-40}
            textAnchor="end"
            interval={0}
            axisLine={false}
            tickLine={false}
            height={36}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "#9ca3af" }}
            tickFormatter={(value) => formatMoney(value)}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="mca_actual"
            name="mca_actual"
            stroke={ACTUAL_COLOR}
            strokeWidth={1.5}
            dot={<ActualDot />}
            activeDot={{ r: 5 }}
            connectNulls={false}
            isAnimationActive={false}
          />
          {showExtrapolated && hasExtrapolated && (
            <Line
              type="monotone"
              dataKey="mca_extrapolated"
              name="mca_extrapolated"
              stroke={EXTRAPOLATED_COLOR}
              strokeWidth={1.5}
              dot={<ExtrapolatedDot />}
              activeDot={{ r: 5 }}
              connectNulls={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
